import { startTransition, useCallback, useContext, useMemo } from 'react';
import { NavigationContext } from './NavigationProvider';
import type {
  ComercialSubview,
  EncerradasFilters,
  NavigationState,
  ObraReturnState,
  OverlayDrawerKind,
  OverlayDrawerMode,
  Selection,
  View,
  ViewParams,
} from './navigation-state';
import { buildUrl, stackToQuery } from './url-bridge';

export interface NavigationActions {
  navigate: (
    view: View,
    options?: { subview?: string | null; viewParams?: ViewParams | null },
  ) => void;
  navigateWithSelection: (
    view: View,
    selection: Partial<Selection>,
    viewParams?: ViewParams | null,
  ) => void;
  goBack: () => void;
  resetToHome: () => void;
  setSubview: (subview: string | null) => void;
  setComercialSubview: (subview: ComercialSubview) => void;
  setEncerradasFilters: (filters: EncerradasFilters) => void;
  setViewParams: (params: ViewParams | null) => void;
  patchViewParams: (patch: Partial<ViewParams>) => void;
  setSelection: (patch: Partial<Selection>) => void;
  setSelectedObraId: (id: string | null) => void;
  setDrawerOportunidadeId: (id: string | null) => void;
  /**
   * Consume the proposta→oportunidade handoff: if `drawerOportunidadeId` is
   * set, push that oportunidade drawer and clear the slot — atomically, in one
   * reducer action. Call it unconditionally from an effect; a second call with
   * nothing pending is a no-op, which is what keeps StrictMode's double-invoked
   * mount effect from stacking two identical drawers.
   */
  consumeDrawerOportunidadeId: () => void;
  setObraReturnState: (state: ObraReturnState | null) => void;
  /**
   * Push a drawer onto the overlay stack. URL is updated via NavigationProvider
   * to reflect the full stack (kind:id[:mode], comma-separated). F5 / paste-link
   * restores the same stack.
   *
   * Session drawers (`SESSION_DRAWER_KINDS` in navigation-state.ts) push without
   * an id — their entity is implicit in the auth/session context. Pass `''`
   * (or omit) for those; the URL serializes as `?drawer=kind` (no `:id`).
   */
  pushOverlayDrawer: (kind: OverlayDrawerKind, id?: string, mode?: OverlayDrawerMode) => void;
  /** Pop the top drawer from the overlay stack. */
  popOverlayDrawer: () => void;
  /** Clear the entire overlay stack at once (e.g. ESC on backdrop). */
  closeAllOverlayDrawers: () => void;
  /**
   * Replace the top entry of the stack in place — same kind+id semantics as
   * pop+push, but the URL is updated via `history.replaceState` so the browser
   * back stack stays small. Used for sibling navigation inside a drawer (e.g.
   * prev/next between `os_servico` siblings); the user expects "back" to close
   * the drawer entirely, not to walk back through every sibling visited.
   */
  replaceTopOverlayDrawer: (kind: OverlayDrawerKind, id: string) => void;
  setOverlayDrawerMode: (mode: OverlayDrawerMode) => void;
  setRequisicaoNotification: (id: string | null, autoOpenSeparacao: boolean) => void;
}


export type UseNavigationResult = NavigationState & { actions: NavigationActions };

export function useNavigation(): UseNavigationResult {
  const ctx = useContext(NavigationContext);
  if (!ctx) {
    throw new Error('useNavigation must be used within <NavigationProvider>');
  }
  const { state, dispatch } = ctx;

  const navigate = useCallback(
    (view: View, options?: { subview?: string | null; viewParams?: ViewParams | null }) =>
      dispatch({
        type: 'NAVIGATE',
        view,
        subview: options?.subview ?? null,
        viewParams: options?.viewParams ?? null,
      }),
    [dispatch],
  );

  const navigateWithSelection = useCallback(
    (view: View, selection: Partial<Selection>, viewParams?: ViewParams | null) =>
      dispatch({
        type: 'NAVIGATE_WITH_SELECTION',
        view,
        selection,
        viewParams: viewParams ?? null,
      }),
    [dispatch],
  );

  const goBack = useCallback(() => dispatch({ type: 'GO_BACK' }), [dispatch]);
  const resetToHome = useCallback(() => dispatch({ type: 'RESET_TO_HOME' }), [dispatch]);

  const setSubview = useCallback(
    (subview: string | null) => dispatch({ type: 'SET_SUBVIEW', subview }),
    [dispatch],
  );

  const setComercialSubview = useCallback(
    (subview: ComercialSubview) => dispatch({ type: 'SET_COMERCIAL_SUBVIEW', subview }),
    [dispatch],
  );

  const setEncerradasFilters = useCallback(
    (filters: EncerradasFilters) => dispatch({ type: 'SET_ENCERRADAS_FILTERS', filters }),
    [dispatch],
  );

  const setViewParams = useCallback(
    (params: ViewParams | null) => dispatch({ type: 'SET_VIEW_PARAMS', params }),
    [dispatch],
  );

  const patchViewParams = useCallback(
    (patch: Partial<ViewParams>) => dispatch({ type: 'PATCH_VIEW_PARAMS', patch }),
    [dispatch],
  );

  const setSelection = useCallback(
    (patch: Partial<Selection>) => dispatch({ type: 'SET_SELECTION', patch }),
    [dispatch],
  );

  const setSelectedObraId = useCallback(
    (id: string | null) => dispatch({ type: 'SET_SELECTED_OBRA_ID', id }),
    [dispatch],
  );

  const setDrawerOportunidadeId = useCallback(
    (id: string | null) => dispatch({ type: 'SET_DRAWER_OPORTUNIDADE_ID', id }),
    [dispatch],
  );

  const consumeDrawerOportunidadeId = useCallback(
    () =>
      // Transition for the same reason as pushOverlayDrawer: this lands a
      // drawer whose lazy chunk may still be in flight.
      startTransition(() => dispatch({ type: 'CONSUME_DRAWER_OPORTUNIDADE_ID' })),
    [dispatch],
  );

  const setObraReturnState = useCallback(
    (obraReturn: ObraReturnState | null) =>
      dispatch({ type: 'SET_OBRA_RETURN_STATE', state: obraReturn }),
    [dispatch],
  );

  const pushOverlayDrawer = useCallback(
    (kind: OverlayDrawerKind, id: string = '', mode?: OverlayDrawerMode) =>
      // Mark drawer pushes as React transitions. When the new drawer's lazy
      // chunk hasn't loaded yet, React keeps the previously committed tree
      // visible until the chunk resolves — instead of flashing the Suspense
      // fallback (null) and unmounting every already-open drawer in the
      // stack. The OverlayDrawerHost's single top-level Suspense then commits
      // the new entry atomically in JSX order, preserving correct stacking.
      startTransition(() =>
        dispatch({ type: 'PUSH_OVERLAY_DRAWER', kind, id, mode }),
      ),
    [dispatch],
  );

  const popOverlayDrawer = useCallback(
    () => dispatch({ type: 'POP_OVERLAY_DRAWER' }),
    [dispatch],
  );

  const closeAllOverlayDrawers = useCallback(
    () => dispatch({ type: 'CLOSE_ALL_OVERLAY_DRAWERS' }),
    [dispatch],
  );

  const replaceTopOverlayDrawer = useCallback(
    (kind: OverlayDrawerKind, id: string) => {
      // The provider's URL-sync effect always uses pushState, so we rewrite
      // history first; by the time the effect re-runs, current === target and
      // the push is skipped. Net result: the in-place swap, no extra history
      // entry. Falls back to a normal push when the stack is empty (the
      // reducer handles the same edge case).
      const stack = state.overlayDrawerStack;
      if (stack.length > 0 && typeof window !== 'undefined') {
        const top = stack[stack.length - 1];
        const newStack = [...stack.slice(0, -1), { ...top, kind, id }];
        const target = buildUrl(state.view, newStack);
        window.history.replaceState(
          { view: state.view, drawer: stackToQuery(newStack) },
          '',
          target,
        );
      }
      // See pushOverlayDrawer for the transition rationale — sibling nav
      // (prev/next inside os_servico, etc.) hits the same lazy-load path.
      startTransition(() =>
        dispatch({ type: 'REPLACE_TOP_OVERLAY_DRAWER', kind, id }),
      );
    },
    [dispatch, state.overlayDrawerStack, state.view],
  );

  const setOverlayDrawerMode = useCallback(
    (mode: OverlayDrawerMode) => dispatch({ type: 'SET_OVERLAY_DRAWER_MODE', mode }),
    [dispatch],
  );

  const setRequisicaoNotification = useCallback(
    (id: string | null, autoOpenSeparacao: boolean) =>
      dispatch({ type: 'SET_REQUISICAO_NOTIFICATION', id, autoOpenSeparacao }),
    [dispatch],
  );

  const actions = useMemo<NavigationActions>(
    () => ({
      navigate,
      navigateWithSelection,
      goBack,
      resetToHome,
      setSubview,
      setComercialSubview,
      setEncerradasFilters,
      setViewParams,
      patchViewParams,
      setSelection,
      setSelectedObraId,
      setDrawerOportunidadeId,
      consumeDrawerOportunidadeId,
      setObraReturnState,
      pushOverlayDrawer,
      popOverlayDrawer,
      closeAllOverlayDrawers,
      replaceTopOverlayDrawer,
      setOverlayDrawerMode,
      setRequisicaoNotification,
    }),
    [
      navigate,
      navigateWithSelection,
      goBack,
      resetToHome,
      setSubview,
      setComercialSubview,
      setEncerradasFilters,
      setViewParams,
      patchViewParams,
      setSelection,
      setSelectedObraId,
      setDrawerOportunidadeId,
      consumeDrawerOportunidadeId,
      setObraReturnState,
      pushOverlayDrawer,
      popOverlayDrawer,
      closeAllOverlayDrawers,
      replaceTopOverlayDrawer,
      setOverlayDrawerMode,
      setRequisicaoNotification,
    ],
  );

  return { ...state, actions };
}
